<script setup>
import { computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import Header from '@/layout/Header.vue'
import Footer from '@/layout/Footer.vue'
import { REVIEWABLE_PAGES } from '@/cms/reviewablePages.js'
// Dark mode (jk-9) parked for launch — 2026-08-05, see
// docs/features/jk-9-dark-mode.md's "Parked for launch" follow-up.
// useTheme() used to be invoked here so its module-level `mode` ref/
// matchMedia listener kept automatic (system-preference) dark mode live
// site-wide. Not calling it means data-theme is never set/updated post-
// hydration, so the OS preference can no longer flip the page's theme.
// The composable itself is untouched — reinstate with:
//   import { useTheme } from '@/composables/useTheme'
//   useTheme()

// Sitewide <title> brand suffix — every page sets a bare label (e.g. "About
// us") and this appends "— Sylvia Rodger Institute" to the tab title. Single
// source of truth: new pages inherit it automatically, no per-page suffix to
// maintain. Home (title = the brand itself) and title-less pages resolve to
// the plain brand, never doubled. Only affects <title>; og:title/twitter:title
// stay the bare label, with brand carried by og:site_name.
useHead({
  titleTemplate: (title) =>
    !title || title === 'Sylvia Rodger Institute'
      ? 'Sylvia Rodger Institute'
      : `${title} — Sylvia Rodger Institute`,
})

const isCmsEnabled = import.meta.env.MODE === 'cms'
const CmsReviewButton = isCmsEnabled
  ? defineAsyncComponent(() => import('@/cms/components/CmsReviewButton.vue'))
  : null

const route = useRoute()
const reviewablePage = computed(() => REVIEWABLE_PAGES.find(p => p.route === route.path))

// jk-37: no separate "Home" nav item — the logo already links to `/`
// (Header.vue's .logo), so it was a redundant second link to the same place.
const navItems = [
  {
    label: 'What we do',
    to: '/what-we-do',
    heading: 'What we do',
    summary: 'Our approach and the work we deliver to create impact',
    children: [
      { label: 'Our approach', description: 'The strengths, values and relationships that guide how we work', to: '/our-approach' },
      { label: 'Our work', description: 'Evidence, collaboration and expertise in action', to: '/our-work' }
    ]
  },
  {
    label: 'The Academy',
    to: '/the-academy',
    heading: 'The Academy',
    summary: 'Autistic-led programs and an alumni network creating positive change',
    children: [
      { label: 'About the Academy', description: 'Our purpose, history and autistic-led governance', to: '/the-academy/about-the-academy' },
      { label: 'Programs', description: 'Our co-designed, autistic-led programs', to: '/the-academy/programs' },
      { label: 'Alumni network', description: 'Meet the Academy alumni shaping communities, the sector and our work', to: '/the-academy/alumni-network' }
    ]
  },
  {
    label: 'Resources and training',
    to: '/resources-and-training',
    heading: 'Resources and training',
    summary: 'National guidance, practical resources and programs that turn knowledge into action',
    children: [
      { label: 'Best Practice Portal', description: 'Evidence-based guidelines and frameworks to support best practice', href: 'https://www.autismcrc.com.au/best-practice', external: true },
      { label: 'eLearning', description: 'Self-paced courses and training modules for professionals', to: '/resources/training' },
      { label: 'Resources', description: 'Practical guides, posters, reports and websites', to: '/resources' }
    ]
  },
  {
    label: 'About us',
    to: '/about',
    heading: 'About us',
    summary: 'Our story, people, and partners, and the latest news from across the Sylvia Rodger Institute',
    children: [
      { label: 'Vision, mission and values', description: 'The vision, mission and values that guide our work', to: '/about/vision-mission-and-values' },
      { label: 'Our story', description: 'The history and the story of our organisation', to: '/about/our-story' },
      { label: 'Our people', description: 'The team behind the Sylvia Rodger Institute', to: '/about/our-people' },
      { label: 'Our network', description: 'The partners and contributors that support our work', to: '/about/our-network' },
      { label: 'News', description: 'The latest news, updates and announcements', to: '/about/news' }
    ]
  },
  { label: 'Contact', to: '/contact' }
]
</script>

<template>
  <div class="app-layout">
    <a class="skip-link" href="#main-content">Skip to main content</a>
    <Header :navItems="navItems" :cmsMode="isCmsEnabled" />
    <CmsReviewButton
      v-if="isCmsEnabled && reviewablePage"
      :key="reviewablePage.path"
      :content-path="reviewablePage.path"
      :label="reviewablePage.label"
    />
    <main id="main-content" class="app-content">
      <RouterView />
    </main>
    <Footer />
  </div>
</template>

<style lang="scss" scoped>
.app-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.app-content {
  flex: 1;
}

// Skip link — hidden off-screen until it receives keyboard focus, then it
// slides into the top-left above the header so keyboard/SR users can jump
// straight past the nav to #main-content.
.skip-link {
  position: absolute;
  top: $space-2;
  left: $space-2;
  z-index: $z-overlay;
  padding: $space-2 $space-4;
  background: var(--color-primary);
  color: var(--color-on-primary);
  border-radius: $radius-sm;
  font-weight: 600;
  text-decoration: none;
  transform: translateY(-150%);
  transition: transform $dur $ease;

  &:focus-visible { @include focus-ring; }

  &:focus {
    transform: translateY(0);
  }
}
</style>
